import React, { useEffect, useState, useCallback, useRef } from "react";
import { supabase } from "lib/supabaseClient";
import { useNavigate, useLocation } from "react-router-dom";
import { insertLog } from "services/logService";
import {
  MdAdd, MdEmail, MdPhone, MdCalendarToday, MdTimer, MdCancel,
  MdPerson, MdWork, MdBadge, MdFingerprint, MdAttachMoney,
  MdLock, MdVerifiedUser, MdDeleteSweep, MdPrint, MdDelete, MdLockReset,
} from "react-icons/md";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/flatpickr.css";
import Card from "components/card";

// ─── Helpers ────────────────────────────────────────────────
const fmt = (d) => {
  if (!d) return "—";
  const dt = new Date(d);
  return isNaN(dt) ? "—" : dt.toLocaleDateString("en-GB");
};
const toSQL = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};
const statusStyle = (s) => ({
  Active:            "bg-green-50 text-green-600 border border-green-200",
  "Annual Leave":    "bg-blue-50 text-blue-600 border border-blue-200",
  "Sick Leave":      "bg-purple-50 text-purple-600 border border-purple-200",
  "Emergency Leave": "bg-orange-50 text-orange-600 border border-orange-200",
}[s] || "bg-gray-100 text-gray-500 border border-gray-200");

const dotColor = (status, onLeave) =>
  onLeave ? "#f97316" : status === "Active" ? "#10b981" : "#6b7280";

const contractStats = (start, end) => {
  if (!start || !end) return { remaining: 0, pct: 0, expired: false };
  const s = new Date(start), e = new Date(end), now = new Date();
  const total     = Math.max(1, Math.floor((e - s) / 86400000));
  const elapsed   = Math.max(0, Math.floor((now - s) / 86400000));
  const remaining = Math.max(0, Math.floor((e - now) / 86400000));
  return { remaining, pct: Math.min(100, (elapsed / total) * 100), expired: now > e };
};

// ─── Sub-components ─────────────────────────────────────────
const EmpAvatar = ({ url, status, onLeave }) => (
  <div className="relative select-none">
    <div className="h-12 w-12 rounded-xl border border-gray-200"
      style={{ backgroundImage: `url(${url || "https://i.ibb.co/mRpg6P0/default-avatar.png"})`, backgroundSize: "cover", backgroundPosition: "center" }} />
    <div className="absolute -bottom-1 -right-1 h-3 w-3 rounded-full border-2 border-white"
      style={{ backgroundColor: dotColor(status, onLeave) }} />
  </div>
);

const ActionBtn = ({ onClick, icon, title, cls }) => (
  <button onClick={onClick} title={title}
    className={`rounded-lg border p-2 transition-all active:scale-90 ${cls}`}>
    {React.cloneElement(icon, { size: 16 })}
  </button>
);

const InputBox = ({ label, value, onChange, type = "text", icon, isDate, placeholder = "" }) => (
  <div className="flex flex-col gap-2 text-left">
    <label className="ml-1 text-[10px] uppercase tracking-widest text-gray-400">{label}</label>
    <div className="relative group">
      {icon && (
        <div className="absolute left-4 top-1/2 -translate-y-1/2 text-[#0055FF] opacity-30 group-focus-within:opacity-70 transition-opacity">
          {React.cloneElement(icon, { size: 16 })}
        </div>
      )}
      {isDate ? (
        <Flatpickr value={value} options={{ dateFormat: "Y-m-d" }}
          onChange={([d]) => d && onChange(toSQL(d))}
          className="w-full rounded-xl border border-gray-200 bg-white p-3.5 pl-11 text-sm text-gray-800 outline-none focus:border-[#0055FF] transition-all" />
      ) : (
        <input type={type} value={value || ""} placeholder={placeholder}
          onChange={e => onChange(e.target.value)}
          className="w-full rounded-xl border border-gray-200 bg-white p-3.5 pl-11 text-sm text-gray-800 outline-none focus:border-[#0055FF] transition-all placeholder:text-gray-300" />
      )}
    </div>
  </div>
);

const DeleteModal = ({ emp, loading, errMsg, onConfirm, onCancel }) => (
  <div className="fixed inset-0 z-[300] flex items-center justify-center bg-black/20 backdrop-blur-sm p-4">
    <div className="w-full max-w-sm rounded-2xl border border-red-100 bg-white p-8 shadow-xl text-center">
      <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-red-50">
        <MdDelete size={24} className="text-red-500" />
      </div>
      <h3 className="mb-1 text-base uppercase tracking-wide text-gray-800">Confirm Deletion</h3>
      <p className="mb-1 text-xs text-gray-400">Permanently deleting:</p>
      <p className="mb-4 text-sm text-red-500">{emp.full_name}</p>
      <p className="mb-6 text-[11px] text-gray-400 leading-relaxed">
        This removes the employee record and login account.<br />
        <span className="text-red-400">This action cannot be undone.</span>
      </p>
      {errMsg && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-500 text-left">
          {errMsg}
        </div>
      )}
      <div className="flex gap-3">
        <button onClick={onCancel}
          className="flex-1 rounded-xl bg-gray-100 py-3 text-xs uppercase tracking-wider text-gray-500 hover:bg-gray-200 transition-all">
          Cancel
        </button>
        <button onClick={onConfirm} disabled={loading}
          className="flex-1 rounded-xl bg-red-500 py-3 text-xs uppercase tracking-wider text-white hover:bg-red-600 transition-all disabled:opacity-50">
          {loading ? "Deleting..." : "Delete"}
        </button>
      </div>
    </div>
  </div>
);

// ═══════════════════════════════════════════════════════════
export default function EmployeesTable() {
  const [employees,     setEmployees]     = useState(() => {
    // تحميل من الكاش فوراً
    try {
      const c = sessionStorage.getItem("core_workforce_employees") || localStorage.getItem("core_workforce_employees");
      return c ? JSON.parse(c) : [];
    } catch { return []; }
  });
  const [currentLeaves, setCurrentLeaves] = useState({});
  const [historyOpen,   setHistoryOpen]   = useState(null);
  const [leaveHistory,  setLeaveHistory]  = useState({});
  const [isAdmin,       setIsAdmin]       = useState(false);
  const [isAuthorized,  setIsAuthorized]  = useState(false);
  const [currentActor,  setCurrentActor]  = useState(null);
  const [loading2,      setLoading2]      = useState(employees.length === 0); // loading فقط إذا الكاش فاضي

  const [showAdd,    setShowAdd]    = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [showPwd,    setShowPwd]    = useState(false);
  const [targetEmp,  setTargetEmp]  = useState(null);
  const [loading,    setLoading]    = useState(false);
  const [errMsg,     setErrMsg]     = useState("");

  const blank = {
    full_name: "", job_title: "", employee_number: "", department: "",
    national_id: "", phone_number: "", email: "", password: "",
    basic_salary: 0, hourly_rate: 0, annual_leave_balance: 30,
    contract_start: "", contract_end: "", role: "employee",
  };
  const [form,     setForm]     = useState(blank);
  const [avatar,   setAvatar]   = useState(null);
  const [contract, setContract] = useState(null);
  const [pwd1,     setPwd1]     = useState("");
  const [pwd2,     setPwd2]     = useState("");

  const navigate = useNavigate();
  const location = useLocation();
  const isMounted = useRef(true);

  const callFn = async (name, body) => {
    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token || "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InhkcXZrZXd3ZmlsbHZmbGRrb3RtIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzA0NjgwMjMsImV4cCI6MjA4NjA0NDAyM30.bFLj9EpcLb3S8uB_3X_Dw0J03IqiQf36zkSQ8Mi0ZpM";
    const res = await fetch("https://xdqvkewwfillvfldkotm.supabase.co/functions/v1/" + name, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "apikey": "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InhkcXZrZXd3ZmlsbHZmbGRrb3RtIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzA0NjgwMjMsImV4cCI6MjA4NjA0NDAyM30.bFLj9EpcLb3S8uB_3X_Dw0J03IqiQf36zkSQ8Mi0ZpM",
        "Authorization": "Bearer " + token,
      },
      body: JSON.stringify(body),
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Function error");
    return json;
  };

  // ── Fetch محسّن بـ Promise.all ──
  const fetchAll = useCallback(async () => {
    try {
      const today = toSQL(new Date());
      const [staffRes, leavesRes] = await Promise.all([
        supabase.from("employees").select("*").order("employee_number"),
        supabase.from("employee_leaves").select("*")
          .eq("status", "approved").lte("start_date", today).gte("end_date", today),
      ]);

      const activeIds = new Set(leavesRes.data?.map(l => l.employee_id) || []);

      // Auto-fix: أي موظف status != Active وما عنده إجازة نشطة → رجّعه Active
      const toFix = (staffRes.data || []).filter(emp =>
        emp.status !== "Active" && !activeIds.has(emp.id)
      );
      if (toFix.length > 0) {
        await Promise.all(toFix.map(emp =>
          supabase.from("employees").update({ status: "Active", is_on_leave: false }).eq("id", emp.id)
        ));
        toFix.forEach(emp => { emp.status = "Active"; emp.is_on_leave = false; });
      }

      const lmap = {};
      leavesRes.data?.forEach(l => { lmap[l.employee_id] = l; });

      if (isMounted.current) {
        setEmployees(staffRes.data || []);
        setCurrentLeaves(lmap);
        setLoading2(false);
        try {
          sessionStorage.setItem("core_workforce_employees", JSON.stringify(staffRes.data || []));
          localStorage.setItem("core_workforce_employees", JSON.stringify(staffRes.data || []));
        } catch {}
      }
    } catch (err) {
      console.error(err);
      if (isMounted.current) setLoading2(false);
    }
  }, []);

  // ── Auth ──
  useEffect(() => {
    isMounted.current = true;
    (async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return navigate("/auth/sign-in");
      const { data: p } = await supabase.from("employees").select("*").eq("id", user.id).single();
      if (p?.role === "admin" || p?.permissions?.can_access_staff) {
        setIsAdmin(p.role === "admin");
        setIsAuthorized(true);
        setCurrentActor({ id: user.id, name: p.full_name, number: p.employee_number, role: p.role });
        fetchAll();
      } else navigate("/admin/default");
    })();
    return () => { isMounted.current = false; };
  }, [location.key, fetchAll]);

  // ── Realtime ──
  useEffect(() => {
    if (!isAuthorized) return;
    const ch = supabase.channel("staff-sync")
      .on("postgres_changes", { event: "*", schema: "public", table: "employees" }, fetchAll)
      .on("postgres_changes", { event: "*", schema: "public", table: "employee_leaves" }, fetchAll)
      .subscribe();
    return () => supabase.removeChannel(ch);
  }, [isAuthorized, fetchAll]);

  const log = async (action, target, details) => {
    try {
      if (!currentActor || !target) return;
      await insertLog({
        timestamp: new Date().toISOString(), actor_name: currentActor.name,
        actor_number: currentActor.number, actor_role: currentActor.role,
        target_id: target.id, target_name: target.full_name,
        target_number: target.employee_number, action, details,
      });
    } catch {}
  };

  // ── ADD ──
  const handleAdd = async () => {
    setErrMsg("");
    if (!form.email || !form.full_name || !form.password) { setErrMsg("Full name, email and password are required."); return; }
    if (form.password.length < 6) { setErrMsg("Password must be at least 6 characters."); return; }
    setLoading(true);
    try {
      const data = await callFn("create-employee", { payload: form });
      const userId = data?.user_id;
      if (avatar && userId) {
        const path = `${userId}/avatar_${Date.now()}.${avatar.name.split(".").pop()}`;
        await supabase.storage.from("contracts").upload(path, avatar);
        const { data: { publicUrl } } = supabase.storage.from("contracts").getPublicUrl(path);
        await supabase.from("employees").update({ avatar_url: publicUrl }).eq("id", userId);
      }
      if (contract && userId) {
        const path = `${userId}/contract_${Date.now()}.${contract.name.split(".").pop()}`;
        await supabase.storage.from("contracts").upload(path, contract);
        const { data: { publicUrl } } = supabase.storage.from("contracts").getPublicUrl(path);
        await supabase.from("employees").update({ contract_pdf_url: publicUrl }).eq("id", userId);
      }
      if (userId && Number(form.hourly_rate) > 0) {
        await supabase.from("employees").update({ hourly_rate: Number(form.hourly_rate) }).eq("id", userId);
      }
      await log("ADD_EMPLOYEE", { id: userId, full_name: form.full_name, employee_number: form.employee_number }, `Added by ${currentActor?.name}`);
      setShowAdd(false); setForm(blank); setAvatar(null); setContract(null);
      await fetchAll();
    } catch (err) { setErrMsg(err.message || "Unknown error"); }
    finally { setLoading(false); }
  };

  // ── DELETE ──
  const handleDelete = async () => {
    if (!targetEmp) return;
    setLoading(true); setErrMsg("");
    try {
      await callFn("delete-employee", { user_id: targetEmp.id });
      const { error: dbErr } = await supabase.from("employees").delete().eq("id", targetEmp.id);
      if (dbErr) throw new Error(dbErr.message);
      await log("DELETE_EMPLOYEE", targetEmp, `Deleted by ${currentActor?.name}`);
      setShowDelete(false); setTargetEmp(null);
      await fetchAll();
    } catch (err) { setErrMsg(err.message); }
    finally { setLoading(false); }
  };

  // ── RESET PASSWORD ──
  const handleResetPwd = async () => {
    setErrMsg("");
    if (!pwd1 || pwd1.length < 6) { setErrMsg("Password must be at least 6 characters."); return; }
    if (pwd1 !== pwd2) { setErrMsg("Passwords do not match."); return; }
    setLoading(true);
    try {
      await callFn("reset-employee-password", { user_id: targetEmp.id, new_password: pwd1 });
      await log("RESET_PASSWORD", targetEmp, `Password reset by ${currentActor?.name}`);
      setShowPwd(false); setTargetEmp(null); setPwd1(""); setPwd2("");
      alert(`✅ Password updated for ${targetEmp.full_name}`);
    } catch (err) { setErrMsg(err.message); }
    finally { setLoading(false); }
  };

  const fetchLeaveHistory = async (empId) => {
    const { data } = await supabase.from("employee_leaves").select("*")
      .eq("employee_id", empId).order("created_at", { ascending: false });
    setLeaveHistory(prev => ({ ...prev, [empId]: data || [] }));
    setHistoryOpen(historyOpen === empId ? null : empId);
  };

  const clearLeaves = async (emp) => {
    if (!isAdmin) return;
    if (!window.confirm(`Wipe all leave history for ${emp.full_name}?`)) return;
    await supabase.from("employee_leaves").delete().eq("employee_id", emp.id);
    await log("CLEAR_LEAVE_HISTORY", emp, "Manual wipe");
    fetchAll();
  };

  // ── PRINT محسّن ──
  const handlePrint = () => {
    const today = new Date();
    const dateStr = today.toLocaleDateString("en-GB", { day: "2-digit", month: "long", year: "numeric" });

    const rows = employees.map(emp => {
      const stats = contractStats(emp.contract_start, emp.contract_end);
      const activeLeave = currentLeaves[emp.id];
      const statusText = activeLeave ? activeLeave.leave_type : (emp.status || "Active");
      const statusColor = {
        "Active": "#10b981",
        "Annual Leave": "#3b82f6",
        "Sick Leave": "#8b5cf6",
        "Emergency Leave": "#f97316",
      }[statusText] || "#6b7280";

      return `
        <tr>
          <td>
            <div class="emp-name">${emp.full_name || "—"}</div>
            <div class="emp-sub">#${emp.employee_number || "—"}</div>
          </td>
          <td>${emp.job_title || "—"}<br><span class="sub">${emp.department || "—"}</span></td>
          <td>${emp.email || "—"}<br><span class="sub">${emp.phone_number || "—"}</span></td>
          <td><span class="badge" style="color:${statusColor};background:${statusColor}18;border:1px solid ${statusColor}30">${statusText}</span></td>
          <td>${fmt(emp.contract_start)}<br><span class="sub">→ ${fmt(emp.contract_end)}</span></td>
          <td>
            ${stats.expired
              ? '<span style="color:#ef4444">Expired</span>'
              : `<div class="bar-wrap"><div class="bar-fill" style="width:${stats.pct}%;background:${stats.remaining <= 30 ? "#ef4444" : stats.remaining <= 90 ? "#f97316" : "#10b981"}"></div></div><span class="sub">${stats.remaining}d left</span>`
            }
          </td>
          <td style="text-align:center">${emp.annual_leave_balance || 0}</td>
          <td style="text-align:center">${emp.annual_leave_used || 0}</td>
          <td style="text-align:center">${emp.sick_leave_count || 0}</td>
        </tr>`;
    }).join("");

    const win = window.open("", "_blank");
    win.document.write(`<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <title>CORE — Workforce Report</title>
  <style>
    @page { size: A4 landscape; margin: 14mm 12mm; }
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: 'Segoe UI', Arial, sans-serif; color: #111827; background: #fff; font-size: 11px; }

    /* ── Header ── */
    .header {
      display: flex; justify-content: space-between; align-items: flex-start;
      padding-bottom: 14px; border-bottom: 2px solid #0055FF; margin-bottom: 20px;
    }
    .brand { font-size: 22px; letter-spacing: 4px; color: #0055FF; text-transform: uppercase; }
    .brand span { color: #111827; opacity: 0.3; font-weight: 300; }
    .report-title { font-size: 10px; letter-spacing: 3px; color: #6b7280; margin-top: 4px; text-transform: uppercase; }
    .meta { text-align: right; font-size: 10px; color: #9ca3af; line-height: 1.9; }
    .meta strong { color: #374151; }

    /* ── Stats Bar ── */
    .stats { display: flex; gap: 20px; margin-bottom: 18px; }
    .stat-box {
      flex: 1; border: 1px solid #e5e7eb; border-radius: 10px;
      padding: 10px 14px; background: #f9fafb;
    }
    .stat-label { font-size: 9px; text-transform: uppercase; letter-spacing: 2px; color: #9ca3af; }
    .stat-value { font-size: 20px; color: #0055FF; margin-top: 2px; }

    /* ── Table ── */
    table { width: 100%; border-collapse: collapse; }
    thead tr { background: #f9fafb; border-bottom: 2px solid #e5e7eb; }
    th {
      padding: 8px 10px; text-align: left; font-size: 9px;
      text-transform: uppercase; letter-spacing: 1.5px; color: #6b7280;
    }
    td { padding: 10px 10px; border-bottom: 1px solid #f3f4f6; vertical-align: middle; }
    tr:hover td { background: #fafafa; }

    .emp-name { font-size: 12px; color: #111827; }
    .emp-sub  { font-size: 9px; color: #9ca3af; margin-top: 2px; }
    .sub      { font-size: 9px; color: #9ca3af; }

    .badge {
      display: inline-block; padding: 2px 8px; border-radius: 6px;
      font-size: 9px; text-transform: uppercase; letter-spacing: 1px;
    }

    .bar-wrap {
      height: 4px; background: #e5e7eb; border-radius: 99px;
      overflow: hidden; margin-bottom: 3px; width: 80px;
    }
    .bar-fill { height: 100%; border-radius: 99px; }

    /* ── Footer ── */
    .footer {
      margin-top: 24px; padding-top: 12px; border-top: 1px solid #e5e7eb;
      display: flex; justify-content: space-between; font-size: 9px; color: #9ca3af;
    }
    .confidential {
      background: #fef3c7; border: 1px solid #fde68a; border-radius: 6px;
      padding: 2px 10px; color: #92400e; font-size: 9px; letter-spacing: 1px;
    }
    .watermark {
      position: fixed; top: 45%; left: 50%; transform: translate(-50%,-50%) rotate(-20deg);
      font-size: 90px; color: #0055FF; opacity: 0.025; white-space: nowrap;
      pointer-events: none; letter-spacing: 10px; text-transform: uppercase;
    }

    @media print {
      body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    }
  </style>
</head>
<body>
  <div class="watermark">CORE PROFESSIONAL</div>

  <div class="header">
    <div>
      <div class="brand">CORE <span>Professional</span></div>
      <div class="report-title">Workforce Compliance Report</div>
    </div>
    <div class="meta">
      <strong>Date:</strong> ${dateStr}<br>
      <strong>Total Staff:</strong> ${employees.length}<br>
      <strong>Generated by:</strong> ${currentActor?.name || "Admin"}<br>
      <span class="confidential">CONFIDENTIAL</span>
    </div>
  </div>

  <div class="stats">
    <div class="stat-box">
      <div class="stat-label">Total Staff</div>
      <div class="stat-value">${employees.length}</div>
    </div>
    <div class="stat-box">
      <div class="stat-label">Active</div>
      <div class="stat-value" style="color:#10b981">${employees.filter(e => !currentLeaves[e.id] && e.status === "Active").length}</div>
    </div>
    <div class="stat-box">
      <div class="stat-label">On Leave</div>
      <div class="stat-value" style="color:#f97316">${Object.keys(currentLeaves).length}</div>
    </div>
    <div class="stat-box">
      <div class="stat-label">Expiring Soon</div>
      <div class="stat-value" style="color:#ef4444">${employees.filter(e => { const s = contractStats(e.contract_start, e.contract_end); return !s.expired && s.remaining <= 30; }).length}</div>
    </div>
    <div class="stat-box">
      <div class="stat-label">Expired Contracts</div>
      <div class="stat-value" style="color:#6b7280">${employees.filter(e => contractStats(e.contract_start, e.contract_end).expired).length}</div>
    </div>
  </div>

  <table>
    <thead>
      <tr>
        <th>Employee</th>
        <th>Position</th>
        <th>Contact</th>
        <th>Status</th>
        <th>Contract Period</th>
        <th>Contract Progress</th>
        <th style="text-align:center">Annual<br>Balance</th>
        <th style="text-align:center">Annual<br>Used</th>
        <th style="text-align:center">Sick<br>Days</th>
      </tr>
    </thead>
    <tbody>${rows}</tbody>
  </table>

  <div class="footer">
    <span>© ${today.getFullYear()} CORE Professional Consulting — All Rights Reserved</span>
    <span>This document is confidential and for internal use only.</span>
    <span>Printed: ${today.toLocaleTimeString("en-GB")}</span>
  </div>

  <script>window.onload = () => window.print();<\/script>
</body>
</html>`);
    win.document.close();
  };

  // ════════════ RENDER ════════════
  if (loading2) return (
    <div className="flex h-[60vh] items-center justify-center">
      <div className="flex flex-col items-center gap-3">
        <div className="h-8 w-8 animate-spin rounded-full border border-[#0055FF] border-t-transparent"></div>
        <p className="text-[10px] uppercase tracking-widest text-gray-400">Loading Staff...</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-white p-4 md:p-6">
      <Card extra="w-full p-6 bg-white border border-gray-200 shadow-sm">

        {/* Header */}
        <div className="mb-8 flex flex-col items-start justify-between gap-4 border-b border-gray-100 pb-6 md:flex-row md:items-center print:hidden">
          <div>
            <h1 className="text-xl uppercase tracking-wider text-gray-800">
              CORE <span className="text-[#0055FF]">Workforce</span>
            </h1>
            <p className="mt-1 text-[10px] uppercase tracking-widest text-gray-400">
              {employees.length} Employees Registered
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={handlePrint}
              className="flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-[11px] uppercase tracking-wider text-gray-500 hover:border-gray-300 hover:bg-gray-50 transition-all">
              <MdPrint size={16} /> Print Report
            </button>
            <button onClick={() => { setForm(blank); setErrMsg(""); setShowAdd(true); }}
              className="flex items-center gap-2 rounded-xl bg-[#0055FF] px-5 py-2.5 text-[11px] uppercase tracking-wider text-white hover:bg-[#0044cc] transition-all">
              <MdAdd size={16} /> Add Employee
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto custom-scrollbar">
          <table className="w-full border-collapse">
            <thead>
              <tr className="border-b border-gray-100 text-[10px] uppercase tracking-widest text-gray-400">
                <th className="pb-4 text-left">Employee</th>
                <th className="pb-4 text-left">Contact</th>
                <th className="pb-4 text-left">Status</th>
                <th className="pb-4 text-left">Hourly Rate</th>
                <th className="pb-4 text-left">Contract</th>
                <th className="pb-4 text-left">Leave Balance</th>
                <th className="pb-4 text-right print:hidden">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {employees.map(emp => {
                const stats      = contractStats(emp.contract_start, emp.contract_end);
                const activeLeave = currentLeaves[emp.id];
                return (
                  <React.Fragment key={emp.id}>
                    <tr className="group transition-colors hover:bg-gray-50/50">

                      {/* Employee */}
                      <td className="py-5">
                        <div className="flex items-center gap-3">
                          <EmpAvatar url={emp.avatar_url} status={emp.status} onLeave={!!activeLeave} />
                          <div>
                            <p className="text-[13px] text-gray-800 group-hover:text-[#0055FF] transition-colors">
                              {emp.full_name}
                            </p>
                            <p className="mt-0.5 text-[10px] text-gray-400">
                              #{emp.employee_number} · {emp.job_title}
                            </p>
                            {activeLeave && (
                              <div className="mt-1.5 flex items-center gap-1.5 rounded-md border border-orange-100 bg-orange-50 px-2 py-0.5 w-fit">
                                <div className="h-1.5 w-1.5 animate-pulse rounded-full bg-orange-400" />
                                <span className="text-[9px] uppercase text-orange-500">
                                  On Leave · Returns {fmt(activeLeave.end_date)}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      </td>

                      {/* Contact */}
                      <td className="py-5">
                        <div className="flex flex-col gap-1.5 text-[11px] text-gray-400">
                          <span className="flex items-center gap-1.5">
                            <MdEmail size={12} className="text-[#0055FF] opacity-60" />
                            {emp.email || "—"}
                          </span>
                          <span className="flex items-center gap-1.5">
                            <MdPhone size={12} className="text-[#0055FF] opacity-60" />
                            {emp.phone_number || "—"}
                          </span>
                        </div>
                      </td>

                      {/* Status */}
                      <td className="py-5">
                        <span className={`rounded-lg px-3 py-1 text-[10px] uppercase tracking-wide ${statusStyle(activeLeave ? activeLeave.leave_type : emp.status)}`}>
                          {activeLeave ? activeLeave.leave_type : emp.status}
                        </span>
                      </td>

                      <td className="py-5">
                        <span className="text-[12px] font-medium text-gray-800">
                          {emp.hourly_rate ? `${emp.hourly_rate} SAR/hr` : "0 SAR/hr"}
                        </span>
                      </td>

                      {/* Contract */}
                      <td className="py-5">
                        <div className="w-40">
                          <div className="flex justify-between text-[9px] text-gray-400 mb-1">
                            <span>{fmt(emp.contract_start)}</span>
                            <span>{fmt(emp.contract_end)}</span>
                          </div>
                          <div className="h-1 w-full rounded-full bg-gray-100 overflow-hidden">
                            <div
                              className={`h-full rounded-full ${stats.expired ? "bg-red-400" : stats.remaining <= 30 ? "bg-orange-400" : stats.remaining <= 90 ? "bg-yellow-400" : "bg-emerald-400"}`}
                              style={{ width: `${stats.pct}%` }}
                            />
                          </div>
                          <p className={`mt-1 text-[10px] ${stats.expired ? "text-red-400" : stats.remaining <= 30 ? "text-orange-400" : "text-gray-400"}`}>
                            {stats.expired ? "Expired" : `${stats.remaining}d remaining`}
                          </p>
                        </div>
                      </td>

                      {/* Leave */}
                      <td className="py-5">
                        <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-[10px]">
                          <div>
                            <span className="text-gray-300 text-[9px] uppercase">Annual</span>
                            <p className="text-emerald-500">{emp.annual_leave_balance || 0}d</p>
                          </div>
                          <div>
                            <span className="text-gray-300 text-[9px] uppercase">Used</span>
                            <p className="text-blue-500">{emp.annual_leave_used || 0}d</p>
                          </div>
                          <div>
                            <span className="text-gray-300 text-[9px] uppercase">Sick</span>
                            <p className="text-purple-400">{emp.sick_leave_count || 0}d</p>
                          </div>
                          <div>
                            <span className="text-gray-300 text-[9px] uppercase">Emergency</span>
                            <p className="text-orange-400">{emp.emergency_leave_count || 0}d</p>
                          </div>
                        </div>
                      </td>

                      {/* Actions */}
                      <td className="py-5 text-right print:hidden">
                        <div className="flex items-center justify-end gap-1.5">
                          <ActionBtn
                            onClick={() => navigate(`/admin/profile?name=${encodeURIComponent(emp.full_name)}`)}
                            icon={<MdVerifiedUser />} title="View Profile"
                            cls="border-blue-100 bg-blue-50 text-blue-500 hover:bg-blue-500 hover:text-white hover:border-blue-500"
                          />
                          {isAdmin && (<>
                            <ActionBtn
                              onClick={() => { setTargetEmp(emp); setPwd1(""); setPwd2(""); setErrMsg(""); setShowPwd(true); }}
                              icon={<MdLockReset />} title="Reset Password"
                              cls="border-gray-200 bg-gray-50 text-gray-400 hover:bg-gray-200 hover:text-gray-600"
                            />
                            <ActionBtn
                              onClick={() => fetchLeaveHistory(emp.id)}
                              icon={<MdTimer />} title="Leave History"
                              cls="border-gray-200 bg-gray-50 text-gray-400 hover:bg-gray-200 hover:text-gray-600"
                            />
                            <ActionBtn
                              onClick={() => clearLeaves(emp)}
                              icon={<MdDeleteSweep />} title="Clear Leave History"
                              cls="border-orange-100 bg-orange-50 text-orange-400 hover:bg-orange-400 hover:text-white"
                            />
                            <ActionBtn
                              onClick={() => { setTargetEmp(emp); setErrMsg(""); setShowDelete(true); }}
                              icon={<MdDelete />} title="Delete Employee"
                              cls="border-red-100 bg-red-50 text-red-400 hover:bg-red-500 hover:text-white"
                            />
                          </>)}
                        </div>
                      </td>
                    </tr>

                    {historyOpen === emp.id && (
                      <tr className="bg-gray-50">
                        <td colSpan={7} className="px-6 py-5">
                          <div className="flex justify-between items-center mb-3">
                            <p className="text-[11px] uppercase tracking-widest text-gray-500">
                              Leave Records — {emp.full_name}
                            </p>
                            <button onClick={() => setHistoryOpen(null)}
                              className="text-[10px] uppercase tracking-wider text-gray-400 hover:text-gray-600">
                              Close
                            </button>
                          </div>
                          <table className="w-full text-[11px]">
                            <thead>
                              <tr className="text-left border-b border-gray-200 text-[9px] uppercase tracking-wider text-gray-400">
                                <th className="pb-2">Type</th>
                                <th className="pb-2">Period</th>
                                <th className="pb-2">Days</th>
                                <th className="pb-2">Status</th>
                                <th className="pb-2">Reason</th>
                              </tr>
                            </thead>
                            <tbody>
                              {(leaveHistory[emp.id] || []).map(l => (
                                <tr key={l.id} className="border-b border-gray-100">
                                  <td className="py-2 text-gray-600">{l.leave_type}</td>
                                  <td className="py-2 text-gray-400">{fmt(l.start_date)} → {fmt(l.end_date)}</td>
                                  <td className="py-2 text-gray-400">{l.days_count}d</td>
                                  <td className="py-2">
                                    <span className={`rounded px-2 py-0.5 text-[9px] uppercase ${
                                      l.status === "approved" ? "bg-green-50 text-green-500" :
                                      l.status === "rejected" ? "bg-red-50 text-red-500" :
                                      "bg-gray-100 text-gray-400"
                                    }`}>{l.status}</span>
                                  </td>
                                  <td className="py-2 text-gray-400 max-w-xs truncate">{l.reason || "—"}</td>
                                </tr>
                              ))}
                              {!(leaveHistory[emp.id]?.length) && (
                                <tr><td colSpan={5} className="py-5 text-center text-gray-300 text-[10px] uppercase tracking-widest">No records</td></tr>
                              )}
                            </tbody>
                          </table>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
              {employees.length === 0 && (
                <tr><td colSpan={7} className="py-16 text-center text-gray-300 text-[11px] uppercase tracking-widest">No employees registered</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* MODAL: ADD */}
      {showAdd && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/20 backdrop-blur-sm p-4">
          <div className="max-h-[92vh] w-full max-w-4xl overflow-y-auto rounded-2xl border border-gray-200 bg-white p-8 shadow-xl custom-scrollbar">
            <div className="mb-8 flex items-center justify-between border-b border-gray-100 pb-5">
              <div>
                <h3 className="text-base uppercase tracking-wider text-gray-800">Add Employee</h3>
                <p className="text-[10px] uppercase tracking-widest text-gray-400 mt-0.5">New Personnel Registration</p>
              </div>
              <button onClick={() => setShowAdd(false)}>
                <MdCancel size={24} className="text-gray-300 hover:text-red-400 transition-colors" />
              </button>
            </div>
            {errMsg && (
              <div className="mb-5 rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-xs text-red-500">{errMsg}</div>
            )}
            <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
              <InputBox label="Full Name *"        value={form.full_name}            onChange={v => setForm({...form, full_name: v})}            icon={<MdPerson />} />
              <InputBox label="Business Email *"   value={form.email}                onChange={v => setForm({...form, email: v})}                icon={<MdEmail />}  type="email" />
              <InputBox label="Password *"         value={form.password}             onChange={v => setForm({...form, password: v})}             icon={<MdLock />}   type="password" placeholder="Min 6 characters" />
              <InputBox label="Employee ID"        value={form.employee_number}      onChange={v => setForm({...form, employee_number: v})}      icon={<MdBadge />} />
              <InputBox label="National ID"        value={form.national_id}          onChange={v => setForm({...form, national_id: v})}          icon={<MdFingerprint />} />
              <InputBox label="Job Title"          value={form.job_title}            onChange={v => setForm({...form, job_title: v})}            icon={<MdWork />} />
              <InputBox label="Phone"              value={form.phone_number}         onChange={v => setForm({...form, phone_number: v})}         icon={<MdPhone />} />
              <InputBox label="Department"         value={form.department}           onChange={v => setForm({...form, department: v})}           icon={<MdWork />} />
              <InputBox label="Basic Salary (SAR)"    value={form.basic_salary}         onChange={v => setForm({...form, basic_salary: v})}                  icon={<MdAttachMoney />}   type="number" />
              <InputBox label="Hourly Rate (SAR/hr)" value={form.hourly_rate}          onChange={v => setForm({...form, hourly_rate: Number(v) || 0})}        icon={<MdTimer />}         type="number" placeholder="e.g. 75" />
              <InputBox label="Annual Leave Days"    value={form.annual_leave_balance} onChange={v => setForm({...form, annual_leave_balance: Number(v)})}    icon={<MdCalendarToday />} type="number" />
              <InputBox label="Contract Start"     value={form.contract_start}       onChange={v => setForm({...form, contract_start: v})}       icon={<MdCalendarToday />} isDate />
              <InputBox label="Contract End"       value={form.contract_end}         onChange={v => setForm({...form, contract_end: v})}         icon={<MdCalendarToday />} isDate />
            </div>

            <div className="mt-5 flex flex-col gap-2 text-left">
              <label className="ml-1 text-[10px] uppercase tracking-widest text-gray-400">Role</label>
              <select value={form.role} onChange={e => setForm({...form, role: e.target.value})}
                className="w-full rounded-xl border border-gray-200 bg-white p-3.5 text-sm text-gray-800 outline-none focus:border-[#0055FF] transition-all">
                <option value="employee">Employee</option>
                <option value="manager">Manager</option>
                <option value="admin">Admin</option>
              </select>
            </div>

            <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="flex flex-col gap-2">
                <label className="text-[10px] uppercase tracking-widest text-gray-400">Profile Photo</label>
                <label className="flex cursor-pointer items-center gap-3 rounded-xl border border-dashed border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-400 hover:bg-gray-100 transition-all">
                  📷 {avatar ? avatar.name : "Choose photo..."}
                  <input type="file" accept="image/*" className="hidden" onChange={e => setAvatar(e.target.files[0])} />
                </label>
              </div>
              <div className="flex flex-col gap-2">
                <label className="text-[10px] uppercase tracking-widest text-gray-400">Contract PDF</label>
                <label className="flex cursor-pointer items-center gap-3 rounded-xl border border-dashed border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-400 hover:bg-gray-100 transition-all">
                  📄 {contract ? contract.name : "Choose PDF..."}
                  <input type="file" accept=".pdf" className="hidden" onChange={e => setContract(e.target.files[0])} />
                </label>
              </div>
            </div>

            <div className="mt-8 flex gap-4">
              <button onClick={() => setShowAdd(false)}
                className="flex-1 rounded-xl bg-gray-100 py-3.5 text-xs uppercase tracking-wider text-gray-500 hover:bg-gray-200 transition-all">
                Cancel
              </button>
              <button onClick={handleAdd} disabled={loading}
                className="flex-[2] rounded-xl bg-[#0055FF] py-3.5 text-xs uppercase tracking-wider text-white hover:bg-[#0044cc] transition-all disabled:opacity-50">
                {loading ? "Creating..." : "Create Employee"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL: DELETE */}
      {showDelete && targetEmp && (
        <DeleteModal emp={targetEmp} loading={loading} errMsg={errMsg}
          onConfirm={handleDelete}
          onCancel={() => { setShowDelete(false); setTargetEmp(null); setErrMsg(""); }} />
      )}

      {/* MODAL: RESET PASSWORD */}
      {showPwd && targetEmp && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/20 backdrop-blur-sm p-4">
          <div className="w-full max-w-sm rounded-2xl border border-gray-200 bg-white p-8 shadow-xl">
            <div className="text-center mb-6">
              <div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-blue-50">
                <MdLock size={20} className="text-blue-500" />
              </div>
              <h3 className="text-sm uppercase tracking-wider text-gray-800">Reset Password</h3>
              <p className="mt-1 text-xs text-gray-400">{targetEmp.full_name}</p>
            </div>
            {errMsg && (
              <div className="mb-4 rounded-lg border border-red-100 bg-red-50 px-3 py-2 text-xs text-red-500">{errMsg}</div>
            )}
            <div className="flex flex-col gap-4">
              <div className="relative">
                <MdLock size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#0055FF] opacity-40" />
                <input type="password" value={pwd1} onChange={e => setPwd1(e.target.value)}
                  placeholder="New password (min 6)"
                  className="w-full rounded-xl border border-gray-200 bg-gray-50 p-3.5 pl-11 text-sm text-gray-800 outline-none focus:border-[#0055FF] placeholder:text-gray-300" />
              </div>
              <div className="relative">
                <MdLock size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#0055FF] opacity-40" />
                <input type="password" value={pwd2} onChange={e => setPwd2(e.target.value)}
                  placeholder="Confirm password"
                  className="w-full rounded-xl border border-gray-200 bg-gray-50 p-3.5 pl-11 text-sm text-gray-800 outline-none focus:border-[#0055FF] placeholder:text-gray-300" />
              </div>
            </div>
            <div className="mt-6 flex gap-3">
              <button onClick={() => { setShowPwd(false); setTargetEmp(null); setErrMsg(""); }}
                className="flex-1 rounded-xl bg-gray-100 py-3 text-xs uppercase tracking-wider text-gray-500 hover:bg-gray-200 transition-all">
                Cancel
              </button>
              <button onClick={handleResetPwd} disabled={loading}
                className="flex-1 rounded-xl bg-[#0055FF] py-3 text-xs uppercase tracking-wider text-white hover:bg-[#0044cc] transition-all disabled:opacity-50">
                {loading ? "Updating..." : "Update"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}